/* global ResizeObserver */

/**
 * Responds to the resize of an element. Uses the performant resizeObserver API (if supported), falling back to an
 * iFrame if not.
 *
 * NOTE consider the implications of using the iFrame fallback. iFrames are heavy objects and usually take a good
 * amount of memory. Be careful and don't abuse it. Currently we only use this for vd-popover and have max 1 instance
 * at a time so this is ok.
 *
 * @function observeResize
 *
 * @param {Element} element
 *        The element to watch.
 *
 * @param {Function} handler
 *        The handler to call when a resize occurs.
 */
export function observeResize(element, handler) {
  if ('ResizeObserver' in window) {
    const elementResizeObserver = new ResizeObserver(entries => {
      // Push the handler into a dedicated animation frame to prevent "ResizeObserver loop limit exceeded" errors in
      // Chrome; see https://github.com/cerner/terra-core/pull/1647
      window.requestAnimationFrame(() => {
        handler(element)
      })
    })
    elementResizeObserver.observe(element)
  } else {
    const frame = document.createElement('iframe')
    frame.className = 'vd-popover-resize-detector'
    frame.onload = () => {
      frame.contentWindow.onresize = () => handler(element)
    }
    element.appendChild(frame)
  }
}

/**
 * Determines if the current browser supports 'passive' event listeners. Events which we indicate to the browser as
 * never calling preventDefault(). This improves scroll performance when using touch events.
 *
 * @function supportsPassiveEvents
 *
 * @return {Boolean} True if passive events are supported, false otherwise.
 */
export function supportsPassiveEvents() {
  let passiveSupported = false

  try {
    const options = Object.defineProperty({}, 'passive', {
      get() {
        passiveSupported = true
      },
    })

    window.addEventListener('vdPassiveTest', options, options)
    window.removeEventListener('vdPassiveTest', options, options)
  } catch (err) {
    passiveSupported = false
  }

  return passiveSupported
}
